import { useMemo } from 'react';
import { Crown, Heart, Users, Bug, MapPin, TrendingUp, TrendingDown, Minus, Sparkles, Shield, Brain, Wind, Zap, Flame, Gem, Cloud, Mountain, Leaf, Hexagon } from 'lucide-react';

interface PlanetState {
  stats?: Record<string, number>;
}

interface CreaturePanelProps {
  surfaceItems: Array<{
    item_id: string;
    animationState?: {
      creatureType?: string;
      creatureStage?: string;
    } | null;
  }>;
  planetState?: PlanetState;
  stage?: number;
  intelligentSpecies?: string | null;
}

interface CreatureGroup {
  type: string;
  colonies: number;
  totalPop: number;
  avgHappiness: number;
  happinessSum: number;
  stages: Record<string, number>;
}

const CREATURE_INFO: Record<string, {
  name: string;
  description: string;
  needs: { label: string; statKey: string; ideal: number; tolerance: number; unit?: string }[];
  color: string;
  bgColor: string;
  icon: string;
}> = {
  blob: {
    name: 'Gelatinous Blob',
    description: 'Simple, resilient lifeforms that thrive in warm, wet environments. They form the foundation of aquatic ecosystems.',
    needs: [
      { label: 'Water', statKey: 'water', ideal: 50, tolerance: 30, unit: '%' },
      { label: 'Temperature', statKey: 'temperature', ideal: 50, tolerance: 25, unit: '°C' },
    ],
    color: 'text-pink-400',
    bgColor: 'bg-pink-950/40 border-pink-800/50',
    icon: 'GB',
  },
  squid: {
    name: 'Deep Squid',
    description: 'Intelligent swimmers of the deep ocean. They require rich mineral waters and cool temperatures.',
    needs: [
      { label: 'Water', statKey: 'water', ideal: 70, tolerance: 20, unit: '%' },
      { label: 'Phosphorus', statKey: 'phosphorus', ideal: 20, tolerance: 10, unit: '%' },
    ],
    color: 'text-purple-400',
    bgColor: 'bg-purple-950/40 border-purple-800/50',
    icon: 'DS',
  },
  shell: {
    name: 'Armoured Shell',
    description: 'Hard-shelled land dwellers. They prefer stable ground and mineral-rich environments.',
    needs: [
      { label: 'Stability', statKey: 'stability', ideal: 50, tolerance: 20, unit: '%' },
      { label: 'Sulphur', statKey: 'sulphur', ideal: 20, tolerance: 10, unit: '%' },
    ],
    color: 'text-amber-400',
    bgColor: 'bg-amber-950/40 border-amber-800/50',
    icon: 'AS',
  },
  wing: {
    name: 'Sky Wing',
    description: 'Aerial creatures that soar on thermal currents. They need open skies and lush vegetation.',
    needs: [
      { label: 'Atmosphere', statKey: 'atmosphere', ideal: 50, tolerance: 25, unit: '%' },
      { label: 'Biomass', statKey: 'biomass', ideal: 40, tolerance: 20, unit: '%' },
    ],
    color: 'text-sky-400',
    bgColor: 'bg-sky-950/40 border-sky-800/50',
    icon: 'SW',
  },
  spike: {
    name: 'Spine Beast',
    description: 'Tough survivors of harsh conditions. They thrive where gentler species cannot endure.',
    needs: [
      { label: 'Temperature', statKey: 'temperature', ideal: 70, tolerance: 30, unit: '°C' },
      { label: 'Stability', statKey: 'stability', ideal: 30, tolerance: 20, unit: '%' },
    ],
    color: 'text-red-400',
    bgColor: 'bg-red-950/40 border-red-800/50',
    icon: 'SB',
  },
  fuzz: {
    name: 'Fuzzling',
    description: 'Fluffy social creatures that spread across lush landscapes. Highly adaptable and fast-breeding.',
    needs: [
      { label: 'Biodiversity', statKey: 'biodiversity', ideal: 50, tolerance: 25, unit: '%' },
      { label: 'Biomass', statKey: 'biomass', ideal: 50, tolerance: 25, unit: '%' },
    ],
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-950/40 border-emerald-800/50',
    icon: 'FZ',
  },
  crystal: {
    name: 'Crystal Form',
    description: 'Silicon-based life that grows like mineral formations. They feed on geothermal energy and rare earths.',
    needs: [
      { label: 'Stability', statKey: 'stability', ideal: 60, tolerance: 20, unit: '%' },
      { label: 'Sulphur', statKey: 'sulphur', ideal: 25, tolerance: 15, unit: '%' },
    ],
    color: 'text-cyan-300',
    bgColor: 'bg-cyan-950/40 border-cyan-800/50',
    icon: 'CF',
  },
  float: {
    name: 'Sky Drifter',
    description: 'Jellyfish-like creatures that float in the upper atmosphere. They harvest water vapour and solar energy.',
    needs: [
      { label: 'Atmosphere', statKey: 'atmosphere', ideal: 60, tolerance: 20, unit: '%' },
      { label: 'Temperature', statKey: 'temperature', ideal: 45, tolerance: 20, unit: '°C' },
    ],
    color: 'text-indigo-300',
    bgColor: 'bg-indigo-950/40 border-indigo-800/50',
    icon: 'SD',
  },
  burrow: {
    name: 'Tunnel Worm',
    description: 'Subterranean creatures that reshape the soil as they tunnel. Their networks aerate the ground.',
    needs: [
      { label: 'Stability', statKey: 'stability', ideal: 40, tolerance: 20, unit: '%' },
      { label: 'Biomass', statKey: 'biomass', ideal: 45, tolerance: 25, unit: '%' },
    ],
    color: 'text-stone-400',
    bgColor: 'bg-stone-950/40 border-stone-800/50',
    icon: 'TW',
  },
  glow: {
    name: 'Lantern Fish',
    description: 'Bioluminescent deep-sea swimmers that light up the ocean depths. They hunt in coordinated packs.',
    needs: [
      { label: 'Water', statKey: 'water', ideal: 75, tolerance: 15, unit: '%' },
      { label: 'Phosphorus', statKey: 'phosphorus', ideal: 25, tolerance: 12, unit: '%' },
    ],
    color: 'text-yellow-300',
    bgColor: 'bg-yellow-950/40 border-yellow-800/50',
    icon: 'LF',
  },
  vine: {
    name: 'Creeper Vine',
    description: 'Semi-sentient plant-creatures that spread across surfaces. They symbiotically support other life.',
    needs: [
      { label: 'Biomass', statKey: 'biomass', ideal: 55, tolerance: 20, unit: '%' },
      { label: 'Water', statKey: 'water', ideal: 50, tolerance: 25, unit: '%' },
    ],
    color: 'text-green-500',
    bgColor: 'bg-green-950/40 border-green-800/50',
    icon: 'CV',
  },
  swarm: {
    name: 'Micro-Swarm',
    description: 'Collectives of tiny organisms that act as one mind. They adapt to almost any environment.',
    needs: [
      { label: 'Biodiversity', statKey: 'biodiversity', ideal: 60, tolerance: 20, unit: '%' },
      { label: 'Oxygen', statKey: 'oxygen', ideal: 40, tolerance: 20, unit: '%' },
    ],
    color: 'text-orange-300',
    bgColor: 'bg-orange-950/40 border-orange-800/50',
    icon: 'MS',
  },
  reefback: {
    name: 'Reefback',
    description: 'Large reef-dwelling grazers that thrive in rich oceans and become careful ecological planners.',
    needs: [
      { label: 'Water', statKey: 'water', ideal: 75, tolerance: 18, unit: '%' },
      { label: 'Biodiversity', statKey: 'biodiversity', ideal: 60, tolerance: 20, unit: '%' },
    ],
    color: 'text-teal-300',
    bgColor: 'bg-teal-950/40 border-teal-800/50',
    icon: 'RB',
  },
  embermoth: {
    name: 'Embermoth',
    description: 'Heat-loving fliers from sulphur-rich regions, drawn to light, ash, and radio towers.',
    needs: [
      { label: 'Temperature', statKey: 'temperature', ideal: 70, tolerance: 20, unit: '°C' },
      { label: 'Sulphur', statKey: 'sulphur', ideal: 28, tolerance: 14, unit: '%' },
    ],
    color: 'text-red-300',
    bgColor: 'bg-red-950/40 border-red-800/50',
    icon: 'EM',
  },
  glasscrawler: {
    name: 'Glasscrawler',
    description: 'Precise mineral-shelled thinkers that favour stable land and crystalline chemistry.',
    needs: [
      { label: 'Stability', statKey: 'stability', ideal: 70, tolerance: 20, unit: '%' },
      { label: 'Phosphorus', statKey: 'phosphorus', ideal: 30, tolerance: 12, unit: '%' },
    ],
    color: 'text-cyan-200',
    bgColor: 'bg-cyan-950/40 border-cyan-800/50',
    icon: 'GC',
  },
  stormray: {
    name: 'Stormray',
    description: 'Atmospheric gliders that ride weather fronts and learn through pressure, sound, and storms.',
    needs: [
      { label: 'Atmosphere', statKey: 'atmosphere', ideal: 70, tolerance: 20, unit: '%' },
      { label: 'Water', statKey: 'water', ideal: 55, tolerance: 25, unit: '%' },
    ],
    color: 'text-blue-300',
    bgColor: 'bg-blue-950/40 border-blue-800/50',
    icon: 'SR',
  },
  tidewalker: {
    name: 'Tidewalker',
    description: 'Amphibious tool users that bridge coasts, rivers, and early settlements.',
    needs: [
      { label: 'Water', statKey: 'water', ideal: 55, tolerance: 20, unit: '%' },
      { label: 'Oxygen', statKey: 'oxygen', ideal: 42, tolerance: 20, unit: '%' },
    ],
    color: 'text-emerald-300',
    bgColor: 'bg-emerald-950/40 border-emerald-800/50',
    icon: 'TW',
  },
  mossbeast: {
    name: 'Mossbeast',
    description: 'Slow social megafauna that cultivate living shelter and protect dense ecosystems.',
    needs: [
      { label: 'Biomass', statKey: 'biomass', ideal: 70, tolerance: 20, unit: '%' },
      { label: 'Biodiversity', statKey: 'biodiversity', ideal: 65, tolerance: 20, unit: '%' },
    ],
    color: 'text-lime-300',
    bgColor: 'bg-lime-950/40 border-lime-800/50',
    icon: 'MB',
  },
  duskfox: {
    name: 'Duskfox',
    description: 'Adaptive land scouts with strong pattern memory, diplomacy, and long-range curiosity.',
    needs: [
      { label: 'Oxygen', statKey: 'oxygen', ideal: 45, tolerance: 20, unit: '%' },
      { label: 'Stability', statKey: 'stability', ideal: 60, tolerance: 25, unit: '%' },
    ],
    color: 'text-violet-300',
    bgColor: 'bg-violet-950/40 border-violet-800/50',
    icon: 'DF',
  },
  ironhorn: {
    name: 'Ironhorn',
    description: 'Mineral-armoured plains builders that value defence, borders, and durable infrastructure.',
    needs: [
      { label: 'Stability', statKey: 'stability', ideal: 75, tolerance: 18, unit: '%' },
      { label: 'Sulphur', statKey: 'sulphur', ideal: 25, tolerance: 15, unit: '%' },
    ],
    color: 'text-stone-300',
    bgColor: 'bg-stone-950/40 border-stone-800/50',
    icon: 'IH',
  },
  skywhale: {
    name: 'Skywhale',
    description: 'Buoyant giants from thick atmospheres whose songs become the first long-distance language.',
    needs: [
      { label: 'Atmosphere', statKey: 'atmosphere', ideal: 80, tolerance: 18, unit: '%' },
      { label: 'Oxygen', statKey: 'oxygen', ideal: 50, tolerance: 18, unit: '%' },
    ],
    color: 'text-sky-200',
    bgColor: 'bg-sky-950/40 border-sky-800/50',
    icon: 'SW',
  },
  frostmole: {
    name: 'Frostmole',
    description: 'Cold-adapted tunnel engineers that store knowledge underground and plan patiently.',
    needs: [
      { label: 'Temperature', statKey: 'temperature', ideal: 35, tolerance: 18, unit: '°C' },
      { label: 'Stability', statKey: 'stability', ideal: 65, tolerance: 20, unit: '%' },
    ],
    color: 'text-slate-300',
    bgColor: 'bg-slate-950/60 border-slate-700/70',
    icon: 'FM',
  },
};

const AMBASSADOR_MAP: Record<string, { name: string; trait: string; icon: React.ReactNode; bonus: string }> = {
  blob: { name: 'Amoeboid Collective', trait: 'Cooperative', icon: <Sparkles className="w-5 h-5 text-pink-400" />, bonus: '+15% population growth' },
  squid: { name: 'Cephalopod Scholars', trait: 'Intelligent', icon: <Brain className="w-5 h-5 text-purple-400" />, bonus: '+20% technology' },
  shell: { name: 'Shell-Armoured Architects', trait: 'Defensive', icon: <Shield className="w-5 h-5 text-amber-400" />, bonus: '+25% stability' },
  wing: { name: 'Avian Sky-City Builders', trait: 'Aerial', icon: <Wind className="w-5 h-5 text-sky-400" />, bonus: '+15% research from aerial observation' },
  spike: { name: 'Spiked Warrior Clan', trait: 'Resilient', icon: <Zap className="w-5 h-5 text-red-400" />, bonus: '+20% biomass preservation' },
  fuzz: { name: 'Fuzzy Tribal Network', trait: 'Social', icon: <Heart className="w-5 h-5 text-rose-400" />, bonus: '+10% happiness' },
  crystal: { name: 'Crystal Hive Mind', trait: 'Harmonic', icon: <Gem className="w-5 h-5 text-cyan-400" />, bonus: '+15% stability & research' },
  float: { name: 'Cloud City Dwellers', trait: 'Aerial', icon: <Cloud className="w-5 h-5 text-indigo-400" />, bonus: '+10% atmosphere & research' },
  burrow: { name: 'Tunnel Republic', trait: 'Industrious', icon: <Mountain className="w-5 h-5 text-stone-400" />, bonus: '+15% land use efficiency' },
  glow: { name: 'Luminous Federation', trait: 'Enlightened', icon: <Flame className="w-5 h-5 text-yellow-400" />, bonus: '+20% energy & happiness' },
  vine: { name: 'Verdant Empire', trait: 'Symbiotic', icon: <Leaf className="w-5 h-5 text-green-400" />, bonus: '+15% biomass & biodiversity' },
  swarm: { name: 'Swarm Collective', trait: 'Adaptive', icon: <Hexagon className="w-5 h-5 text-orange-400" />, bonus: '+10% all stats during crises' },
  reefback: { name: 'Reefback Covenant', trait: 'Ecological', icon: <Leaf className="w-5 h-5 text-teal-300" />, bonus: '+20% conservation scoring' },
  embermoth: { name: 'Embermoth Signal-Seers', trait: 'Transformative', icon: <Flame className="w-5 h-5 text-red-300" />, bonus: '+15% signal reach' },
  glasscrawler: { name: 'Glasscrawler Logic Weavers', trait: 'Precise', icon: <Gem className="w-5 h-5 text-cyan-200" />, bonus: '+20% scan confidence' },
  stormray: { name: 'Stormray Weather Speakers', trait: 'Dynamic', icon: <Wind className="w-5 h-5 text-blue-300" />, bonus: '+15% exploration during storms' },
  tidewalker: { name: 'Tidewalker Bridge Envoys', trait: 'Amphibious', icon: <Users className="w-5 h-5 text-emerald-300" />, bonus: '+15% envoy success' },
  mossbeast: { name: 'Mossbeast Grove Wardens', trait: 'Patient', icon: <Leaf className="w-5 h-5 text-lime-300" />, bonus: '+20% biodiversity recovery' },
  duskfox: { name: 'Duskfox Pattern Scouts', trait: 'Strategic', icon: <Brain className="w-5 h-5 text-violet-300" />, bonus: '+15% diplomacy and scouting' },
  ironhorn: { name: 'Ironhorn Shield Captains', trait: 'Resolute', icon: <Shield className="w-5 h-5 text-stone-300" />, bonus: '+20% defence scoring' },
  skywhale: { name: 'Skywhale Chorus Diplomats', trait: 'Expansive', icon: <Cloud className="w-5 h-5 text-sky-200" />, bonus: '+20% peaceful first contact' },
  frostmole: { name: 'Frostmole Deep Planners', trait: 'Enduring', icon: <Mountain className="w-5 h-5 text-slate-300" />, bonus: '+15% long mission stability' },
};

const CREATURE_ASSET_PATHS: Record<string, string> = {
  blob: '/assets/creatures/creature_blob_adult.png',
  squid: '/assets/creatures/creature_squid_adult.png',
  shell: '/assets/creatures/creature_shell_adult.png',
  wing: '/assets/creatures/creature_wing_adult.png',
  spike: '/assets/creatures/creature_spike_adult.png',
  fuzz: '/assets/creatures/creature_fuzz_adult.png',
  crystal: '/assets/creatures/creature_crystal_adult.png',
  float: '/assets/creatures/creature_float_adult.png',
  burrow: '/assets/creatures/creature_burrow_adult.png',
  glow: '/assets/creatures/creature_glow_adult.png',
  vine: '/assets/creatures/creature_vine_adult.png',
  swarm: '/assets/creatures/creature_swarm_adult.png',
  reefback: '/assets/creatures/creature_reefback_adult.png',
  embermoth: '/assets/creatures/creature_embermoth_adult.png',
  glasscrawler: '/assets/creatures/creature_glasscrawler_adult.png',
  stormray: '/assets/creatures/creature_stormray_adult.png',
  tidewalker: '/assets/creatures/creature_tidewalker_adult.png',
  mossbeast: '/assets/creatures/creature_mossbeast_adult.png',
  duskfox: '/assets/creatures/creature_duskfox_adult.png',
  ironhorn: '/assets/creatures/creature_ironhorn_adult.png',
  skywhale: '/assets/creatures/creature_skywhale_adult.png',
  frostmole: '/assets/creatures/creature_frostmole_adult.png',
};

const CREATURE_AMBASSADOR_ASSET_PATHS: Record<string, string> = {
  blob: '/assets/creatures/creature_blob_ambassador.png',
  squid: '/assets/creatures/creature_squid_ambassador.png',
  shell: '/assets/creatures/creature_shell_ambassador.png',
  wing: '/assets/creatures/creature_wing_ambassador.png',
  spike: '/assets/creatures/creature_spike_ambassador.png',
  fuzz: '/assets/creatures/creature_fuzz_ambassador.png',
  crystal: '/assets/creatures/creature_crystal_ambassador.png',
  float: '/assets/creatures/creature_float_ambassador.png',
  burrow: '/assets/creatures/creature_burrow_ambassador.png',
  glow: '/assets/creatures/creature_glow_ambassador.png',
  vine: '/assets/creatures/creature_vine_ambassador.png',
  swarm: '/assets/creatures/creature_swarm_ambassador.png',
  reefback: '/assets/creatures/creature_reefback_ambassador.png',
  embermoth: '/assets/creatures/creature_embermoth_ambassador.png',
  glasscrawler: '/assets/creatures/creature_glasscrawler_ambassador.png',
  stormray: '/assets/creatures/creature_stormray_ambassador.png',
  tidewalker: '/assets/creatures/creature_tidewalker_ambassador.png',
  mossbeast: '/assets/creatures/creature_mossbeast_ambassador.png',
  duskfox: '/assets/creatures/creature_duskfox_ambassador.png',
  ironhorn: '/assets/creatures/creature_ironhorn_ambassador.png',
  skywhale: '/assets/creatures/creature_skywhale_ambassador.png',
  frostmole: '/assets/creatures/creature_frostmole_ambassador.png',
};

const CREATURE_SPACE_AMBASSADOR_ASSET_PATHS: Record<string, string> = {
  blob: '/assets/creatures/creature_blob_space_ambassador.png',
  squid: '/assets/creatures/creature_squid_space_ambassador.png',
  shell: '/assets/creatures/creature_shell_space_ambassador.png',
  wing: '/assets/creatures/creature_wing_space_ambassador.png',
  spike: '/assets/creatures/creature_spike_space_ambassador.png',
  fuzz: '/assets/creatures/creature_fuzz_space_ambassador.png',
  crystal: '/assets/creatures/creature_crystal_space_ambassador.png',
  float: '/assets/creatures/creature_float_space_ambassador.png',
  burrow: '/assets/creatures/creature_burrow_space_ambassador.png',
  glow: '/assets/creatures/creature_glow_space_ambassador.png',
  vine: '/assets/creatures/creature_vine_space_ambassador.png',
  swarm: '/assets/creatures/creature_swarm_space_ambassador.png',
  reefback: '/assets/creatures/creature_reefback_space_ambassador.png',
  embermoth: '/assets/creatures/creature_embermoth_space_ambassador.png',
  glasscrawler: '/assets/creatures/creature_glasscrawler_space_ambassador.png',
  stormray: '/assets/creatures/creature_stormray_space_ambassador.png',
  tidewalker: '/assets/creatures/creature_tidewalker_space_ambassador.png',
  mossbeast: '/assets/creatures/creature_mossbeast_space_ambassador.png',
  duskfox: '/assets/creatures/creature_duskfox_space_ambassador.png',
  ironhorn: '/assets/creatures/creature_ironhorn_space_ambassador.png',
  skywhale: '/assets/creatures/creature_skywhale_space_ambassador.png',
  frostmole: '/assets/creatures/creature_frostmole_space_ambassador.png',
};

type CreatureAvatarVariant = 'adult' | 'ambassador' | 'spaceAmbassador';

export function CreatureAvatar({ type, variant = 'adult', className = 'w-10 h-10' }: { type: string; variant?: CreatureAvatarVariant; className?: string }) {
  const info = CREATURE_INFO[type];
  const assetPath = variant === 'spaceAmbassador'
    ? CREATURE_SPACE_AMBASSADOR_ASSET_PATHS[type] || CREATURE_AMBASSADOR_ASSET_PATHS[type] || CREATURE_ASSET_PATHS[type]
    : variant === 'ambassador'
      ? CREATURE_AMBASSADOR_ASSET_PATHS[type] || CREATURE_ASSET_PATHS[type]
      : CREATURE_ASSET_PATHS[type];

  return (
    <div className={`shrink-0 rounded-full flex items-center justify-center overflow-hidden ${className} ${info?.bgColor || 'bg-slate-800 border border-slate-700'}`}>
      {assetPath ? (
        <img
          src={assetPath}
          alt=""
          aria-hidden="true"
          className="w-full h-full object-contain p-0.5"
          draggable={false}
        />
      ) : (
        <span className={`text-xs font-bold ${info?.color || 'text-slate-400'}`}>{info?.icon || '??'}</span>
      )}
    </div>
  );
}

function calculateHappiness(creatureType: string, stats: Record<string, number>): number {
  const info = CREATURE_INFO[creatureType];
  if (!info) return 50;

  let totalScore = 0;
  let totalWeight = 0;

  for (const need of info.needs) {
    const value = stats[need.statKey] || 0;
    const diff = Math.abs(value - need.ideal);
    const score = Math.max(0, 1 - diff / need.tolerance);
    totalScore += score;
    totalWeight += 1;
  }

  return Math.round((totalScore / totalWeight) * 100);
}

function formatNeedValue(value: number, unit?: string): string {
  if (unit === '°C') return `${Math.round(value - 35)}°C`;
  return `${Math.round(value)}${unit || ''}`;
}

export function buildCreatureGroups(
  surfaceItems: CreaturePanelProps['surfaceItems'],
  stats: Record<string, number>,
  biomass: number,
  intelligentSpecies?: string | null,
): CreatureGroup[] {
  const groups: Record<string, CreatureGroup> = {};

  surfaceItems
    .filter((item) => item.animationState?.creatureType)
    .forEach((item) => {
      const type = item.animationState!.creatureType!;
      const stage = item.animationState!.creatureStage || 'baby';
      const happiness = calculateHappiness(type, stats);
      const basePop = stage === 'baby' ? 10 : stage === 'child' ? 50 : 120;
      const population = Math.round(basePop * (0.5 + happiness / 200) + biomass * 0.5);

      if (!groups[type]) {
        groups[type] = {
          type,
          colonies: 0,
          totalPop: 0,
          avgHappiness: 0,
          happinessSum: 0,
          stages: {},
        };
      }

      groups[type].colonies++;
      groups[type].totalPop += population;
      groups[type].happinessSum += happiness;
      groups[type].stages[stage] = (groups[type].stages[stage] || 0) + 1;
    });

  Object.values(groups).forEach((group) => {
    group.avgHappiness = Math.round(group.happinessSum / group.colonies);
  });

  return Object.values(groups)
    .filter((group) => group.type !== intelligentSpecies)
    .sort((a, b) => b.totalPop - a.totalPop);
}

export function getAmbassadorCreature(
  intelligentSpecies: string | null | undefined,
  creatureGroups: CreatureGroup[],
  dominantCreature?: CreatureGroup,
): CreatureGroup | undefined {
  if (!intelligentSpecies) return dominantCreature;
  return creatureGroups.find((group) => group.type === intelligentSpecies) || {
    type: intelligentSpecies,
    colonies: 0,
    totalPop: 0,
    avgHappiness: 0,
    happinessSum: 0,
    stages: {},
  };
}

export default function CreaturePanel({ surfaceItems, planetState, stage = 0, intelligentSpecies }: CreaturePanelProps) {
  const stats = planetState?.stats || {};
  const biomass = stats.biomass || 0;

  // Group surface items by creature type
  const creatureGroups = useMemo(() => {
    return buildCreatureGroups(surfaceItems, stats, biomass, intelligentSpecies);
  }, [surfaceItems, stats, biomass, intelligentSpecies]);

  const dominantCreature = creatureGroups[0];
  const ambassadorCreature = getAmbassadorCreature(intelligentSpecies, creatureGroups, dominantCreature);
  const totalCreatures = creatureGroups.reduce((sum, g) => sum + g.totalPop, 0);
  const hasAmbassador = stage >= 5 && Boolean(intelligentSpecies);

  if (creatureGroups.length === 0 && !hasAmbassador) {
    return (
      <div className="p-4">
        <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
          <Bug className="w-5 h-5 text-green-400" />
          Creatures
        </h2>
        <div className="text-slate-400 text-sm text-center py-8">
          <Bug className="w-8 h-8 mx-auto mb-2 text-slate-600" />
          No creatures yet. Start life-category development projects to introduce species.
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <h2 className="text-lg font-bold flex items-center gap-2">
        <Bug className="w-5 h-5 text-green-400" />
        Creatures
      </h2>

      {/* Colony summary */}
      {creatureGroups.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-slate-800/60 rounded-lg p-2.5 text-center border border-slate-700">
            <div className="text-xs text-slate-400">Species</div>
            <div className="text-lg font-bold text-white">{creatureGroups.length}</div>
          </div>
          <div className="bg-slate-800/60 rounded-lg p-2.5 text-center border border-slate-700">
            <div className="text-xs text-slate-400">Colonies</div>
            <div className="text-lg font-bold text-white">
              {creatureGroups.reduce((sum, g) => sum + g.colonies, 0)}
            </div>
          </div>
          <div className="bg-slate-800/60 rounded-lg p-2.5 text-center border border-slate-700">
            <div className="text-xs text-slate-400">Total Pop</div>
            <div className="text-lg font-bold text-white">{totalCreatures.toLocaleString()}</div>
          </div>
        </div>
      )}

      {/* Dominant species */}
      {dominantCreature && (
        <div className="p-3 rounded-xl bg-gradient-to-r from-yellow-900/40 to-amber-900/40 border border-yellow-700/50">
          <div className="flex items-center gap-2 mb-2">
            <Crown className="w-4 h-4 text-yellow-400" />
            <span className="text-xs font-semibold text-yellow-400 uppercase tracking-wider">Dominant Species</span>
          </div>
          <div className="flex items-center gap-3">
            <CreatureAvatar type={dominantCreature.type} />
            <div>
              <div className="font-semibold text-sm">{CREATURE_INFO[dominantCreature.type]?.name || dominantCreature.type}</div>
              <div className="text-xs text-slate-400">
                {dominantCreature.colonies} {dominantCreature.colonies === 1 ? 'colony' : 'colonies'} • {dominantCreature.totalPop.toLocaleString()} population
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ambassador section at Stage 5+ */}
      {hasAmbassador && ambassadorCreature && (
        <div className="p-3 rounded-xl bg-gradient-to-r from-amber-900/30 to-yellow-900/30 border border-amber-600/40">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <span className="text-xs font-semibold text-amber-400 uppercase tracking-wider">Civilisation Ambassador</span>
          </div>
          <div className="flex items-center gap-3">
            <CreatureAvatar type={ambassadorCreature.type} variant="ambassador" />
            <div className="flex-1">
              <div className="font-semibold text-sm text-amber-100">
                {AMBASSADOR_MAP[ambassadorCreature.type]?.name || CREATURE_INFO[ambassadorCreature.type]?.name || ambassadorCreature.type}
              </div>
              <div className="flex items-center gap-2 text-xs text-amber-300/70">
                <span className="uppercase tracking-wider">{AMBASSADOR_MAP[ambassadorCreature.type]?.trait || 'Unknown'}</span>
                <span>•</span>
                <span className="text-emerald-400/80">{AMBASSADOR_MAP[ambassadorCreature.type]?.bonus || ''}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Creature cards grouped by type */}
      <div className="space-y-3">
        {creatureGroups.map((group) => {
          const info = CREATURE_INFO[group.type];
          if (!info) return null;

          const isDominant = group.type === dominantCreature?.type;
          const happinessColor = group.avgHappiness >= 70 ? 'text-green-400' : group.avgHappiness >= 40 ? 'text-yellow-400' : 'text-red-400';
          const happinessBar = group.avgHappiness >= 70 ? 'bg-green-500' : group.avgHappiness >= 40 ? 'bg-yellow-500' : 'bg-red-500';
          const dominance = totalCreatures > 0 ? Math.round((group.totalPop / totalCreatures) * 100) : 0;

          return (
            <div
              key={group.type}
              className={`p-3 rounded-xl border transition-all ${info.bgColor} ${isDominant ? 'ring-1 ring-yellow-500/50' : ''}`}
            >
              <div className="flex items-start gap-3">
                <CreatureAvatar type={group.type} />

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <h3 className={`font-semibold text-sm ${info.color}`}>{info.name}</h3>
                      {isDominant && <Crown className="w-3.5 h-3.5 text-yellow-400" />}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-slate-500">
                      <MapPin className="w-3 h-3" />
                      {group.colonies} {group.colonies === 1 ? 'colony' : 'colonies'}
                    </div>
                  </div>

                  <p className="text-[11px] text-slate-400 mt-0.5 line-clamp-2" title={info.description}>{info.description}</p>

                  {/* Stats row */}
                  <div className="flex items-center gap-3 mt-2">
                    <div className="flex items-center gap-1 text-xs text-slate-300">
                      <Users className="w-3 h-3 text-slate-500" />
                      {group.totalPop.toLocaleString()}
                    </div>
                    <div className="flex items-center gap-1 text-xs">
                      <Heart className={`w-3 h-3 ${happinessColor}`} />
                      <span className={happinessColor}>{group.avgHappiness}% happy</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-slate-300">
                      <span className="text-[10px] text-slate-500">Share</span>
                      <span className="font-mono">{dominance}%</span>
                    </div>
                  </div>

                  {/* Happiness bar */}
                  <div className="mt-1.5 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${happinessBar}`} style={{ width: `${group.avgHappiness}%` }} />
                  </div>

                  {/* Needs */}
                  <div className="mt-2 space-y-1">
                    {info.needs.map((need) => {
                      const currentValue = stats[need.statKey] || 0;
                      const diff = currentValue - need.ideal;
                      const met = Math.abs(diff) <= need.tolerance;
                      const TrendIcon = diff > need.tolerance * 0.3 ? TrendingUp : diff < -need.tolerance * 0.3 ? TrendingDown : Minus;
                      const trendColor = met ? 'text-green-400' : diff > 0 ? 'text-yellow-400' : 'text-red-400';

                      return (
                        <div key={need.statKey} className="flex items-center justify-between text-[10px]">
                          <div className="flex items-center gap-1.5">
                            <span className="text-slate-400">{need.label}</span>
                            <TrendIcon className={`w-3 h-3 ${trendColor}`} />
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-slate-500">
                              ideal {formatNeedValue(need.ideal, need.unit)}
                            </span>
                            <span className={`font-mono ${trendColor}`}>
                              {formatNeedValue(currentValue, need.unit)}
                            </span>
                            {met ? (
                              <span className="text-green-500">✓</span>
                            ) : (
                              <span className="text-red-400">needs {diff > 0 ? 'less' : 'more'}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
